import React from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/safeSupabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { subscriptionPillClass, subscriptionStatusLabel } from '@/lib/subscriptionStatus';
import { CreditCard, Users, Zap } from 'lucide-react';

export interface SeatUsage {
  unlimited: boolean;
  pool_org_id?: string;
  pool_name?: string;
  used?: number;
  max?: number;
}

export default function OrgBilling() {
  const { org } = useAuth();

  // Seats belong to the top org of the branch (the IMO) and cover its whole
  // downline, so this org's own member count is not the number that fills
  // up. get_seat_usage() reads the same count the invite check enforces
  // (20260925000004). A failed read hides the section rather than showing
  // a number the limit does not use.
  const { data: seats } = useQuery({
    queryKey: ['org-seat-usage', org?.id],
    queryFn: async () => {
      if (!org) return null;
      const { data, error } = await (supabase.rpc as unknown as (
        fn: 'get_seat_usage',
        args: { p_org: string },
      ) => Promise<{ data: SeatUsage | null; error: Error | null }>)('get_seat_usage', { p_org: org.id });
      if (error) throw error;
      return data;
    },
    enabled: !!org,
  });

  if (!org) {
    return <Card><CardContent className="py-12 text-center text-muted-foreground">No account found.</CardContent></Card>;
  }

  const limited = !!seats && !seats.unlimited && typeof seats.used === 'number' && typeof seats.max === 'number' && seats.max > 0;
  const seatPct = limited ? Math.min(100, Math.round((seats.used! / seats.max!) * 100)) : 0;
  const seatsFull = limited && seats.used! >= seats.max!;
  const sharedPool = limited && seats.pool_org_id !== org.id;
  // An exempt org owes nothing, so the payment controls are noise on their
  // page. They stay for everyone else — this hides them, it does not remove
  // the feature (Drew, 2026-09-22).
  const isExempt = org.subscription_status === 'exempt';
  const planName = org.subscription_plan ?? 'standard';
  // Was: active → default, trialing → secondary, EVERYTHING ELSE → destructive.
  // That painted `exempt` red on the owner's own Billing page while the Admin
  // Panel showed it green (Drew, 2026-09-22). Both now read one map.

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            Billing & Subscription
          </CardTitle>
          <CardDescription>Manage your plan and usage</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Plan Info */}
          <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Current Plan</p>
              <p className="text-xl font-bold capitalize">{planName}</p>
            </div>
            <span className={`inline-block font-semibold capitalize text-sm px-3 py-1 rounded-full whitespace-nowrap ${subscriptionPillClass(org.subscription_status)}`}>
              {subscriptionStatusLabel(org.subscription_status)}
            </span>
          </div>

          {/* Seat Usage */}
          {seats?.unlimited && (
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Seat Usage</span>
              <span className="text-sm text-muted-foreground ml-auto">Unlimited</span>
            </div>
          )}
          {limited && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Seat Usage</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {seats.used} / {seats.max} seats
                </span>
              </div>
              <Progress value={seatPct} className="h-2" />
              {sharedPool && (
                <p className="text-xs text-muted-foreground">
                  Shared across everyone under {seats.pool_name}.
                </p>
              )}
              {seatsFull ? (
                <p className="text-xs text-destructive">
                  You've reached your seat limit. Contact support to add seats.
                </p>
              ) : seatPct >= 80 && (
                <p className="text-xs text-amber-600">
                  You're approaching your seat limit. Contact support to add seats.
                </p>
              )}
            </div>
          )}

          {/* Actions — billed orgs only. The line that used to sit under this
              button named an unshipped payment processor: a note Drew left
              himself that every customer could read. */}
          {!isExempt && (
            <div className="pt-2">
              <Button disabled className="gap-2">
                <Zap className="h-4 w-4" />
                Manage Subscription
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Contact support to change your plan.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
